// Zustand des Edit-Fensters (nur lokal, nicht im Projekt): Edit-Modus, Werkzeug, Raster, Nudge, Auswahl,
// Zwischenablage, offene Fenster, Strip-Silence-Vorschau, aufgeklappte Take-Lanes.
import { create } from 'zustand';
import { useStudio } from './store.ts';
import { expandEdit } from '../edit/mixops.ts';
import type { Clipboard, EditMode, Range, Tool, FadeOpts } from '../edit/ops.ts';
import { DEFAULT_FADES } from '../edit/ops.ts';
import { DEFAULT_STRIP, type StripOpts } from '../edit/analysis.ts';
import { DEFAULT_BEAT, type BeatOpts, type Resolution } from '../edit/beat.ts';

export type Dialog = null | 'strip' | 'fades' | 'repeat' | 'spot' | 'gain' | 'rename' | 'time' | 'markers' | 'loop' | 'events' | 'shortcuts' | 'beat' | 'tempo' | 'audiosuite' | 'equant' | 'vtiming' | 'commit' | 'export';

/** Was im Edit-Fenster zu sehen ist (wie das View-Menü in Pro Tools) */
export interface EditView {
  /** Spurliste links */
  tracks: boolean;
  /** Spalte Inserts A–E an den Spurköpfen */
  inserts: boolean;
  /** Lineale */
  minsec: boolean;
  tempo: boolean;
  chords: boolean;
  /** Seitenleiste rechts: Raum, Clip-Liste, Memory Locations; null = eingeklappt */
  side: 'room' | 'clips' | 'markers' | 'groups' | null;
  /** Dock unten (Takes, MIDI-Editor) eingeklappt */
  dockClosed: boolean;
}
export const DEFAULT_VIEW: EditView = { tracks: false, inserts: true, minsec: false, tempo: true, chords: true, side: 'room', dockClosed: false };

export interface EditState {
  mode: EditMode;
  tool: Tool;
  grid: string;
  /** In Shuffle, Slip und Spot zusätzlich am Raster einrasten (Snap to Grid) */
  snap: boolean;
  nudge: string;
  follow: boolean;
  laneH: number;
  /** Tab springt zum nächsten Anschlag (Tab to Transient) statt nur zur nächsten Clip-Grenze */
  tabTransient: boolean;
  range: Range | null;
  clipboard: Clipboard | null;
  fades: FadeOpts;
  strip: StripOpts;
  stripPreview: Map<string, [number, number][]> | null;
  beat: BeatOpts;
  /** Beat-Detective-Vorschau: Trigger und (beim Conform) ihr Ziel */
  beatPreview: { t: number; cls: Resolution; to?: number }[] | null;
  dialog: Dialog;
  /** Ziel eines Dialogs (z. B. Clip für Spot) */
  dialogArg?: string;
  /** Spuren mit aufgeklappten Take- und Playlist-Lanes */
  lanes: string[];
  /** Spuren in der Warp-Ansicht (Elastic Audio: Event- und Warp-Marker) */
  warp: string[];
  /** sichtbare Automation-Lanes je Spur (Parameter-Ids) */
  autoLanes: Record<string, string[]>;
  view: EditView;
  setView(v: Partial<EditView>): void;
  set(p: Partial<EditState>): void;
  setRange(r: Range | null): void;
  open(d: Dialog, arg?: string): void;
}

const KEY = 'srs.edit';
const saved = (() => { try { return JSON.parse(localStorage.getItem(KEY) ?? '{}'); } catch { return {}; } })() as Partial<EditState>;
const persist = (s: EditState) => { try { localStorage.setItem(KEY, JSON.stringify({ mode: s.mode, tool: s.tool, grid: s.grid, snap: s.snap, nudge: s.nudge, follow: s.follow, laneH: s.laneH, tabTransient: s.tabTransient, fades: s.fades, strip: s.strip, beat: s.beat, view: s.view })); } catch { /* privat */ } };

export const useEdit = create<EditState>()((set, get) => ({
  mode: saved.mode ?? 'grid',
  tool: saved.tool ?? 'smart',
  tabTransient: saved.tabTransient ?? true,
  grid: saved.grid ?? '1/16',
  snap: saved.snap ?? false,
  nudge: saved.nudge ?? '1/16',
  follow: saved.follow ?? true,
  laneH: saved.laneH ?? 73,
  range: null,
  clipboard: null,
  fades: saved.fades ?? DEFAULT_FADES,
  strip: saved.strip ?? DEFAULT_STRIP,
  stripPreview: null,
  beat: { ...DEFAULT_BEAT, ...(saved.beat ?? {}) },
  beatPreview: null,
  dialog: null,
  lanes: [],
  warp: [],
  autoLanes: {},
  view: { ...DEFAULT_VIEW, ...(saved.view ?? {}) },
  setView(v) { set({ view: { ...get().view, ...v } }); persist(get()); },
  set(p) { set(p); persist(get()); },
  // Edit-Gruppen: Auswahl auf einer Spur gilt für alle Spuren ihrer aktiven Edit-Gruppen
  setRange(range) { set({ range: range && range.trackIds.length ? { ...range, trackIds: expandEdit(useStudio.getState().project, range.trackIds) } : range }); },
  open(dialog, dialogArg) { set({ dialog, dialogArg }); },
}));

export const MODE_LABEL: Record<EditMode, string> = { shuffle: 'Shuffle', slip: 'Slip', spot: 'Spot', grid: 'Grid' };
export const TOOL_LABEL: Record<Tool, string> = { smart: 'Smart Tool', trim: 'Trim', select: 'Selector', grab: 'Grabber', scrub: 'Scrubber', pencil: 'Pencil', cut: 'Schere' };
